import { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  BarChart3,
  Building,
  ChevronDown,
  CreditCard,
  FileText,
  LayoutDashboard,
  Lock,
  LogOut,
  Plus,
  Settings,
  Users,
  MapPin,
  Truck,
  Gauge,
  Download,
  Cpu,
  Brain,
  Bot,
  Zap,
  Link,
  TrendingUp,
  Code,
  Database,
  HeadphonesIcon,
  ListTodo,
  Activity,
  GitBranch,
  Timer,
  TestTube,
  Upload
} from "lucide-react";
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useAuth } from '@/hooks/useAuth';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import { useModules } from "@/contexts/ModulesContext";
import { buildNavFromModules } from "@/lib/menuMapping";
import { Skeleton } from "@/components/ui/skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

// Legacy static navigation as fallback when no module config is available
const navigationItems = (basePrefix: string) => [
  {
    title: "Dashboard",
    items: [
      { title: "Dashboard", url: `${basePrefix}`, icon: LayoutDashboard },
    ]
  },
  {
    title: "Customer Management",
    items: [
      { title: "Partners", url: `${basePrefix}/partners`, icon: Building, permission: "onboarding" },
      { title: "Customers", url: `${basePrefix}/customers`, icon: Users, permission: "onboarding" },
      { title: "Sites", url: `${basePrefix}/sites`, icon: MapPin, permission: "onboarding" },
      { title: "Suppliers", url: `${basePrefix}/suppliers`, icon: Truck, permission: "onboarding" },
      { title: "Accounts", url: `${basePrefix}/accounts`, icon: CreditCard, permission: "onboarding" },
      { title: "Meters", url: `${basePrefix}/meters`, icon: Gauge, permission: "onboarding" },
    ]
  },
  {
    title: "Bill Collection",
    items: [      
      //{ title: "Bill Queue", url: `${basePrefix}/bill-queue`, icon: ListTodo, permission: "bill_collection" },
      { title: "Manual Upload", url: `${basePrefix}/manual-upload`, icon: Upload, permission: "bill_collection" },
      { title: "Bills", url: `${basePrefix}/bills`, icon: FileText, permission: "bill_collection" },
      // { title: "Collection Dashboard", url: `${basePrefix}/collection`, icon: BarChart3, permission: "bill_collection" },
      // { title: "Collection Activities", url: `${basePrefix}/collection-activities`, icon: Activity, permission: "bill_collection" },
      // { title: "Collection Workflows", url: `${basePrefix}/collection-workflows`, icon: GitBranch, permission: "bill_collection" },
    ]
  },
  {
    title: "Parser Workflows",
    items: [
      // { title: "Parser Sandbox", url: `${basePrefix}/parser-sandbox`, icon: TestTube, permission: "bill_processing" },
    ]
  },
  {
    title: "Sandbox",
    items: [
      { title: "Collection Sandbox", url: `${basePrefix}/collector-sandbox`, icon: TestTube, permission: "bill_collection" },
      { title: "Parser Sandbox", url: `${basePrefix}/parser-sandbox`, icon: TestTube, permission: "bill_processing" },
    ]
  },
  // {
  //   title: "Bill Processing",
  //   items: [
  //     { title: "Processing", url: `${basePrefix}/processing`, icon: Cpu, permission: "bill_processing" },
  //   ]
  // },  
  {
    title: "AI & Automation",
    items: [
      { title: "AI Parser", url: `${basePrefix}/ai-parser`, icon: Brain, permission: "ai_parser" },
      { title: "AI Agents", url: `${basePrefix}/ai-agents`, icon: Bot, permission: "ai_agents" },
      { title: "Automation", url: `${basePrefix}/automation`, icon: Zap, permission: "automation" },
    ]
  },
  {
    title: "Reports & Analytics", 
    items: [
      { title: "Reports", url: `${basePrefix}/reports`, icon: BarChart3, permission: "reporting" },
      { title: "Analytics", url: `${basePrefix}/analytics`, icon: TrendingUp, permission: "analytics" },
      { title: "Enterprise Analytics", url: `${basePrefix}/enterprise-analytics`, icon: TrendingUp, permission: "enterprise_analytics" },
      { title: "Compliance", url: `${basePrefix}/compliance`, icon: Lock, permission: "compliance" },
    ]
  },
  {
    title: "Integration & API",
    items: [
      { title: "Integrations", url: `${basePrefix}/integrations`, icon: Link, permission: "integrations" },
      { title: "API Access", url: `${basePrefix}/api`, icon: Code, permission: "api" },
    ]
  },
  {
    title: "Storage & Support",
    items: [
      { title: "Enhanced Storage", url: `${basePrefix}/storage`, icon: Database, permission: "storage" },
      { title: "Priority Support", url: `${basePrefix}/support`, icon: HeadphonesIcon, permission: "support" },
    ]
  },
  {
    title: "Workspace Admin",
    items: [
      { title: "Trial Management", url: `${basePrefix}/trial`, icon: Timer, permission: "trial_management" },
      { title: "Access Control (New)", url: `${basePrefix}/access-control-new`, icon: Lock, permission: "workspace_admin" },
      { title: "Access Control", url: `${basePrefix}/access-control`, icon: Lock, permission: "workspace_admin" },
      { title: "Bulk Operations", url: `${basePrefix}/bulk-operations`, icon: Zap, permission: "bulk_operations" },
      { title: "Audit Logs", url: `${basePrefix}/audit-logs`, icon: FileText, permission: "audit_logs" },
      { title: "Security Monitoring", url: `${basePrefix}/security-monitoring`, icon: Lock, permission: "security_monitoring" },
      { title: "Role Management", url: `${basePrefix}/role-management`, icon: Users, permission: "role_management" },
      { title: "Multi-Tenant", url: `${basePrefix}/multi-tenant`, icon: Building, permission: "multi_tenant" },
      { title: "Workspace Settings", url: `${basePrefix}/workspace-settings`, icon: Settings, permission: "workspace_admin" },
    ]
  }
];

interface PartnerBranding {
  id: string;
  name: string;
  brand_logo_url?: string;
  brand_primary_color?: string;
  brand_secondary_color?: string;
}

interface AppSidebarProps {
  partnerBranding?: PartnerBranding | null;
}

export function AppSidebar({ partnerBranding }: AppSidebarProps = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const sidebar = useSidebar();
  const collapsed = sidebar.state === 'collapsed';
  const { currentWorkspace, contextStatus, isLoading: workspaceLoading, hasPermissionSync, clearWorkspace } = useWorkspace();
  const { user, signOut } = useAuth();
  const moduleAccess = useModuleAccess('workspace', currentWorkspace?.id || '');
  const modulesCtx = useModules();
  
  // Permission checks temporarily disabled until backend workspace permissions are finalized
  const permissionChecksEnabled = false;

  // Build base prefix & groups early so we can initialize collapsed state based on active route
  const workspaceId = currentWorkspace?.id || '';
  const basePrefix = location.pathname.startsWith('/dashboard')
    ? '/dashboard'
    : (workspaceId ? `/w/${workspaceId}/dashboard` : '/dashboard');
  // Build groups strictly from API modules (no static fallback for testing)
  const groups = buildNavFromModules(modulesCtx?.modules || [], basePrefix);

  // Determine which group contains the active route
  // If the same route exists in multiple groups (e.g., Manual Parser also points
  // to parser-sandbox), prefer the more specific "Sandbox" group so the Sandbox
  // section stays open when visiting sandbox pages.
  // Safely resolve the initially active group; handle case when there are no groups
  // First, try to match only groups that contain the active route AND an enabled item
  let matchedGroups = groups.filter(g => g.items.some(i => location.pathname === i.url && (i as any).enabled !== false));
  // If nothing matched (e.g., current route comes from a disabled item mapping), fall back to any match
  if (matchedGroups.length === 0) {
    matchedGroups = groups.filter(g => g.items.some(i => location.pathname === i.url));
  }
  const activeGroupTitle = (
    matchedGroups.find(g => g.title.toLowerCase() === 'sandbox')?.title ||
    matchedGroups.find(g => g.items.some(i => i.url.includes('-sandbox')))?.title ||
    matchedGroups[0]?.title ||
    groups[0]?.title ||
    ''
  );

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() =>
    groups.reduce((acc, g) => { acc[g.title] = g.title === activeGroupTitle; return acc; }, {} as Record<string, boolean>)
  );

  // Keep groups collapse state in sync when route changes (only one open)
  useEffect(() => {
    setOpenGroups(prev => {
      const next: Record<string, boolean> = {};
      groups.forEach(g => { next[g.title] = g.title === activeGroupTitle; });
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, basePrefix]);

  // Hoist handleLogout so we don't reference it before declaration in JSX
  async function handleLogout() {
    try {
      console.log('\ud83d\udd04 Starting logout process...');

      // Clear workspace data first
      if (clearWorkspace) {
        clearWorkspace();
      }

      await signOut();
      console.log('\u2705 Logout successful, navigating to auth page...');

  // Clear durable workspace backup as part of logout
  try { localStorage.removeItem('lastWorkspaceId'); } catch (e) {}
  // Navigate to auth page (signOut already clears state)
      navigate('/auth', { replace: true });

    } catch (error) {
      console.error('\u274c Logout error:', error);
      // Even if logout fails, clear session storage and in-memory auth store and navigate
      try { sessionStorage.removeItem('pendingUsername'); } catch (e) {}
      try { sessionStorage.removeItem('currentWorkspaceId'); } catch (e) {}
      try { sessionStorage.removeItem('workspaceData'); } catch (e) {}
      try { sessionStorage.removeItem('userData'); } catch (e) {}
      try { localStorage.removeItem('lastWorkspaceId'); } catch (e) {}
      try { require('@/services/authStore').setAuthStore({ authToken: null, pendingUsername: null, userData: null, workspaceData: null }); } catch (e) {}
      navigate('/auth', { replace: true });
    }
  }

  console.log('🎯 [AppSidebar] Navigation debug:', {
    workspaceId,
    currentWorkspace: currentWorkspace?.name,
    isOwner: moduleAccess.isOwner,
  totalNavigationItems: groups.reduce((total, group) => total + group.items.length, 0),
  totalGroups: groups.length,
    usingDynamicModules: true,
    subscription: modulesCtx?.subscription,
    moduleAccessData: {
      enabledModulesCount: moduleAccess.enabledModules?.length || 0,
      enabledModules: moduleAccess.enabledModules,
      isLoading: moduleAccess.isLoading
    },
    allPermissionsInNavigation: groups
      .flatMap(group => group.items)
      .map(item => (item as any).permission)
      .filter(Boolean)
  });

  // Build groups directly with a robust base prefix
  // groups already computed above

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const getNavCls = (
    path: string, 
    isEnabled: boolean
  ) => {
    const baseClasses = "group flex w-full items-center rounded-md px-2 py-2 text-sm font-medium transition-colors";
    
    if (!isEnabled) {
      return `${baseClasses} text-muted-foreground cursor-pointer opacity-60 hover:opacity-80`;
    }
    
    if (isActive(path)) {
      return `${baseClasses} bg-primary text-primary-foreground hover:bg-primary/90`;
    }
    
    return `${baseClasses} text-foreground hover:bg-muted hover:text-foreground`;
  };

  // Enforce single-open behavior but allow closing the active group by toggling it off
  const toggleGroup = (groupTitle: string, desiredOpen?: boolean) => {
    setOpenGroups(prev => {
      const isCurrentlyOpen = !!prev[groupTitle];
      // If desiredOpen provided by Collapsible, use it; otherwise invert current
      const willOpen = typeof desiredOpen === 'boolean' ? desiredOpen : !isCurrentlyOpen;
      const next: Record<string, boolean> = {};
      groups.forEach(g => { next[g.title] = false; });
      if (willOpen) {
        next[groupTitle] = true; // open clicked group and close others
      }
      // else: leave all closed
      return next;
    });
  };

  const hasPermission = (_moduleId?: string): boolean => {
    if (!permissionChecksEnabled) return true; // short-circuit while disabled
    // Original logic retained for future reactivation:
    // if (contextStatus === 'loading' || !currentWorkspace) return true;
    // if (!_moduleId) return true;
    // if (moduleAccess.isOwner) return true;
    // return moduleAccess.hasModule(_moduleId);
    return true;
  };

  const handleNavigation = (url: string, moduleId?: string) => {
    if (!hasPermission(moduleId || '')) {
      // Could show access request dialog here
      return;
    }
    navigate(url);
  };

  // (function body hoisted above)

  return (
    <Sidebar 
      className={cn("border-r transition-all duration-200 ease-in-out")}
      collapsible="icon"
    >
      {/* Header */}
      <SidebarHeader className="border-b px-4 py-3">
        <div className="flex items-center space-x-2">
          <div className="flex-shrink-0">
            {partnerBranding?.brand_logo_url ? (
              <img 
                src={partnerBranding.brand_logo_url} 
                alt={`${partnerBranding.name} logo`} 
                className="h-8 w-8 rounded object-contain"
                onError={(e) => {
                  // Fallback to BP icon if partner logo fails to load
                  e.currentTarget.style.display = 'none';
                  const fallback = document.createElement('div');
                  fallback.className = 'h-8 w-8 rounded bg-primary flex items-center justify-center';
                  fallback.innerHTML = '<span class="text-sm font-bold text-primary-foreground">BP</span>';
                  e.currentTarget.parentNode.appendChild(fallback);
                }}
              />
            ) : (
              <img 
                src="/brand/billportal-icon.svg" 
                alt="BillPortal" 
                className="h-8 w-8 rounded"
                onError={(e) => {
                  // Fallback to original if image fails to load
                  e.currentTarget.style.display = 'none';
                  const fallback = document.createElement('div');
                  fallback.className = 'h-8 w-8 rounded bg-primary flex items-center justify-center';
                  fallback.innerHTML = '<span class="text-sm font-bold text-primary-foreground">B</span>';
                  e.currentTarget.parentNode.appendChild(fallback);
                }}
              />
            )}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold truncate">
                {partnerBranding?.name || 'BillPortal'}
              </h2>
              <p className="text-xs text-muted-foreground truncate">
                {partnerBranding ? 'Powered by BillPortal' : currentWorkspace?.name}
              </p>
            </div>
          )}
        </div>
      </SidebarHeader>

  <SidebarContent className="px-2 overflow-y-auto">
        {/* Quick Action Button */}
        {!collapsed && (() => {
          // Determine access to Manual Upload submodule
          const billCollection = groups.find(g => g.title === 'Bill Collection');
          const manualUpload = billCollection?.items.find(i => i.title === 'Manual Upload');
          const manualUploadEnabled = !!manualUpload && (manualUpload as any).enabled !== false && hasPermission((manualUpload as any).permission || '');

          if (manualUploadEnabled) {
            return (
              <div className="p-2">
                <Button 
                  variant="hero" 
                  size="sm" 
                  className="w-full justify-start"
                  onClick={() => navigate(`${basePrefix}/upload`)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Bill
                </Button>
              </div>
            );
          }

          // Locked state when Manual Upload is not accessible
          return (
            <div className="p-2">
              <Button
                variant="outline"
                size="sm"
                disabled
                className="w-full justify-start opacity-70 cursor-not-allowed"
                title="You don't have access to Manual Upload"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Bill
                <Lock className="h-3 w-3 ml-auto" />
              </Button>
            </div>
          );
        })()}

        {/* Navigation Groups (expanded view) */}
        {!collapsed && groups.length === 0 && (
          <div className="px-2 pt-2 text-xs text-muted-foreground">
            No modules available for this workspace yet.
          </div>
        )}
        {!collapsed && (
        <div className="space-y-1">
          {groups.map((group) => {
            // Special case: Dashboard should be a direct button without a dropdown
            if (group.title === 'Dashboard') {
              const item = group.items[0];
              // Dashboard has no permission gate; treat as enabled
              const isEnabled = (item as any).enabled !== false;
              return (
                <SidebarGroup key={group.title}>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          className={getNavCls(item.url, isEnabled)}
                          onClick={() => isEnabled && handleNavigation(item.url)}
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                          {/* No lock shown for Dashboard */}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              );
            }

            // Default rendering for all other groups (collapsible)
            return (
              <SidebarGroup key={group.title}>
                <Collapsible
                  open={!!openGroups[group.title]}
                  onOpenChange={(open) => toggleGroup(group.title, open)}
                >
                  <SidebarGroupLabel asChild>
                    <CollapsibleTrigger className="group/collapsible w-full">
                      <div className="flex items-center w-full">
                        <span className="text-xs font-semibold uppercase tracking-wider">
                          {group.title}
                        </span>
                        <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=closed]/collapsible:rotate-180" />
                      </div>
                    </CollapsibleTrigger>
                  </SidebarGroupLabel>
                  <CollapsibleContent>
                    <SidebarGroupContent>
                      <SidebarMenu>
                        {group.items.map((item) => {
                          const isEnabled = (item as any).enabled !== false && hasPermission(item.permission || '');
                          return (
                            <SidebarMenuItem key={item.title}>
                              <SidebarMenuButton
                                className={getNavCls(item.url, isEnabled)}
                                onClick={() => isEnabled && handleNavigation(item.url, item.permission)}
                              >
                                <item.icon className="h-4 w-4" />
                                <span>{item.title}</span>
                                {!isEnabled && !moduleAccess.isOwner && (
                                  <Lock className="h-3 w-3 ml-auto" />
                                )}
                              </SidebarMenuButton>
                            </SidebarMenuItem>
                          );
                        })}
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </CollapsibleContent>
                </Collapsible>
              </SidebarGroup>
            );
          })}
        </div>
        )}

        {/* Collapsed Navigation */}
        {collapsed && (
          <div className="space-y-1 mt-1 overflow-y-auto pr-1">
            {groups.map((group) => {
              return group.items.map((item) => {
                const isEnabled = (item as any).enabled !== false && hasPermission(item.permission || '');
                
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      size="sm"
                      className={getNavCls(item.url, isEnabled)}
                      onClick={() => isEnabled && handleNavigation(item.url, item.permission)}
                      title={`${group.title}: ${item.title}${!isEnabled ? ' (Locked)' : ''}`}
                    >
                      <item.icon className="h-4 w-4" />
                      {!isEnabled && !moduleAccess.isOwner && <Lock className="h-2 w-2 absolute -bottom-0.5 -right-0.5" />}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              });
            })}
          </div>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={handleLogout}
              className="w-full justify-start text-muted-foreground hover:text-foreground hover:bg-muted"
            >
              <LogOut className="h-4 w-4" />
              {!collapsed && <span>Logout</span>}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}